import React from "react";
import { ImageOff } from "lucide-react";
import { PortfolioImage } from "@/hooks/useProfile";
import { useValidImageUrl } from "./useValidImageUrl";
import { useIsMobile } from "@/hooks/use-mobile";
import { convertSupabaseUrlToRenderUrl } from "@/utils/imageUtils";

interface ThumbnailsProps {
  images: PortfolioImage[];
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  imageError: Record<string, boolean>;
  handleImageError: (imageId: string, url?: string) => void;
  vertical?: boolean;
}

const Thumbnails = ({
  images,
  currentIndex,
  setCurrentIndex,
  imageError,
  handleImageError,
  vertical = false,
}: ThumbnailsProps) => {
  const { isValidImageUrl } = useValidImageUrl();
  const isMobile = useIsMobile();

  if (images.length <= 1) {
    return null;
  }

  // Defined fixed sizes for thumbnails based on device type
  const thumbWidth = isMobile ? 70 : 120;
  const thumbHeight = isMobile ? 52 : 90;

  return (
    <div
      className={`
        ${
          vertical
            ? "flex flex-col space-y-2 md:space-y-4"
            : "flex space-x-2 md:space-x-4"
        } 
        py-2 
        ${
          vertical
            ? "overflow-y-auto max-h-[calc(100vh-200px)]"
            : "overflow-x-auto"
        } 
        no-scrollbar
        ${vertical ? "pr-2" : ""}
      `}
      style={{
        paddingBottom: "10px",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {images.map((image, index) => {
        const isValid = isValidImageUrl(image.url);
        return (
          <button
            key={image.id || `thumb-${index}`}
            onClick={() => setCurrentIndex(index)}
            className={`relative rounded-md overflow-hidden flex-shrink-0 border-2 transition-all ${
              currentIndex === index
                ? "border-primary shadow-md ring-2 ring-primary/30"
                : "border-transparent hover:border-primary/50"
            }`}
            aria-label={`View image ${index + 1}`}
            style={{
              width: `${thumbWidth}px`,
              height: `${thumbHeight}px`,
              minWidth: `${thumbWidth}px`,
            }}
          >
            {isValid && !imageError[image.id] ? (
              <img
                src={convertSupabaseUrlToRenderUrl(image.url, 30)}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                onError={() => handleImageError(image.id, image.url)}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-muted">
                <ImageOff className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            {currentIndex === index && (
              <div className="absolute inset-0 bg-primary/10"></div>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default Thumbnails;
